import { useCallback, useEffect, useState } from "react";
import { getEmptyDateRange } from "../constants";
import { buildCdpFilterPayload, isSameDateRange, toDateRangeValue } from "../utils";

export function useCdpFilters({ applyOnFieldChange = false, initialApplied = null } = {}) {
  const [filters, setFilters] = useState({ domain: null, platform: null });
  const [dateValue, setDateValue] = useState(getEmptyDateRange);
  const [appliedFilters, setAppliedFilters] = useState(initialApplied);

  const handleApply = useCallback(() => {
    setAppliedFilters(buildCdpFilterPayload(filters, dateValue));
  }, [dateValue, filters]);

  const handleReset = useCallback(() => {
    setFilters({ domain: null, platform: null });
    setDateValue(getEmptyDateRange());
    setAppliedFilters({});
  }, []);

  const handleDateSelect = useCallback((date) => {
    const next = toDateRangeValue(date);
    setDateValue((prev) => (isSameDateRange(prev, next) ? prev : next));
  }, []);

  const setDomain = useCallback((domain) => {
    setFilters((prev) => (prev.domain?.id === domain?.id ? prev : { ...prev, domain }));
  }, []);

  const setPlatform = useCallback((platform) => {
    if (typeof platform === "function") return;
    setFilters((prev) => (prev.platform?.id === platform?.id ? prev : { ...prev, platform }));
  }, []);

  useEffect(() => {
    if (!applyOnFieldChange) return;
    setAppliedFilters((prev) => {
      const next = buildCdpFilterPayload(filters, dateValue);
      if (
        prev?.product_id === next.product_id &&
        prev?.registration_device === next.registration_device &&
        prev?.start_date === next.start_date &&
        prev?.end_date === next.end_date
      ) {
        return prev;
      }
      return next;
    });
    // Dates still wait for Apply; this only syncs product/platform.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyOnFieldChange, filters.domain, filters.platform]);

  return {
    filters,
    dateValue,
    appliedFilters,
    handleApply,
    handleReset,
    handleDateSelect,
    setDomain,
    setPlatform,
  };
}
